import type { VerifiedCredentialCategory } from 'src/types/graphql';

import {
  supportedVerifiableCredentialTypenames,
  type ProfileVerifications,
  type Verifiable,
  type VerifiableCredential,
  type VerificationsNudge,
} from './types';

const isSupportedNudge = (
  nudge: NonNullable<NonNullable<ProfileVerifications>['nudge']>,
): nudge is VerificationsNudge =>
  nudge.__typename === 'VerificationsOpenDetailsNudge' ||
  ('credential' in nudge &&
    (nudge.credential.__typename === 'IdentityVerifiableCredential' ||
      nudge.credential.__typename === 'WorkplaceVerifiableCredential'));

export const processNudge = (
  nudge: NonNullable<ProfileVerifications>['nudge'] | undefined,
): VerificationsNudge | null => {
  if (nudge && isSupportedNudge(nudge)) {
    return nudge;
  }
  return null;
};

export const processVerified = (
  verified: VerifiedCredentialCategory[] | null | undefined,
) => {
  const verifiedCredentials = verified?.flatMap(
    (verifiedItem) => verifiedItem.credentials,
  );

  const verifiedCredentialCount = verifiedCredentials?.length;

  return { verified, verifiedCredentialCount };
};

const isSupportedVerifiableCredential = (
  credential: VerifiableCredential,
): credential is VerifiableCredential =>
  supportedVerifiableCredentialTypenames.has(credential.__typename);

const filterSupportedVerifiable = (
  verifiable: Verifiable | null | undefined,
) => {
  if (!verifiable) {
    return verifiable;
  }
  const supportedVerifiable: Verifiable = [];

  verifiable.forEach((category) => {
    const { credentials, pendingCount } = category;
    const supportedCredentials =
      credentials?.filter((credential) =>
        isSupportedVerifiableCredential(credential),
      ) ?? []; // New and unsupported interfaces will be filtered out

    // Only show category if it has credentials or pending count to show
    if (supportedCredentials.length > 0 || (pendingCount && pendingCount > 0)) {
      const supportedCategory = {
        ...category,
        credentials: supportedCredentials,
      };
      supportedVerifiable.push(supportedCategory);
    }
  });

  return supportedVerifiable;
};

export const processVerifiable = (
  verifiable: Verifiable | null | undefined,
) => {
  // Filter out categories with no credentials OR pendingCount
  // This is because graph can implement a new type of credential and the section might be empty
  const filteredVerifiable = filterSupportedVerifiable(verifiable);

  const verifiableCredentials = filteredVerifiable?.flatMap(
    (verifiableItem) => verifiableItem.credentials,
  );
  const verifiableCredentialCount = verifiableCredentials?.length;

  return { verifiable: filteredVerifiable, verifiableCredentialCount };
};
